class CartAddonProducts extends HTMLElement {
    constructor() {
        super();
        this.buttonText = 'Add to Cart';
        this.isInitialized = false;
    }

    connectedCallback() {
        this.retryCount = 0;
        this.maxRetries = 5;
        this.initializeWithRetry();
    }

    initializeWithRetry() {
        const productContainers = this.querySelectorAll('.eam-cc-addon-product');
        
        if (productContainers.length === 0 && this.retryCount < this.maxRetries) {
            this.retryCount++;
            setTimeout(() => {
                this.initializeWithRetry();
            }, 100 * this.retryCount);
            return;
        }

        this.setupVariantSelection();
    }

    disconnectedCallback() {
        this.isInitialized = false;
    }

    setupVariantSelection() {
        if (this.isInitialized) {
            return;
        }

        const productContainers = this.querySelectorAll('.eam-cc-addon-product');
        
        productContainers.forEach(container => {
            const selects = container.querySelectorAll('.eam-cc-addon-variant-select');
            const freeSockSelect = container.querySelector('.eam-cc-free-sock-variant-select');
            const form = container.querySelector('form[action*="/cart/add"]');
            
            selects.forEach(select => {
                select.addEventListener('change', () => {
                    this.updateVariantSelection(container);
                });
            });

            if (freeSockSelect) {
                freeSockSelect.addEventListener('change', () => {
                    this.updateVariantSelection(container);
                });
            }

            if (form && form.dataset.freeSockSubmitBound !== 'true') {
                form.addEventListener('submit', this.onAddonFormSubmit.bind(this), true);
                form.dataset.freeSockSubmitBound = 'true';
            }
        });

        this.isInitialized = true;
    }

    async onAddonFormSubmit(event) {
        const form = event.currentTarget;
        if (!(form instanceof HTMLFormElement)) return;

        const addButton = form.querySelector('.eam-cc-addon-product-button');
        if (!(addButton instanceof HTMLButtonElement)) return;
        if (addButton.dataset.addWithFreeSock !== 'true') return;

        const productContainer = form.closest('.eam-cc-addon-product');
        if (!productContainer) return;

        const variantIdInput = form.querySelector('.eam-cc-addon-variant-id');
        const mainVariantId = parseInt(variantIdInput?.value || '', 10);
        if (!mainVariantId) return;

        const freeSockSelect = productContainer.querySelector('.eam-cc-free-sock-variant-select');
        const freeSockVariantId = parseInt(freeSockSelect?.value || '', 10);
        if (!freeSockVariantId) {
            event.preventDefault();
            event.stopImmediatePropagation();
            addButton.disabled = true;
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const propertyKey = addButton.dataset.freeSockPropertyKey || 'Promotion';
        const propertyValue = addButton.dataset.freeSockPropertyValue || 'Free with TILO';
        const previousButtonText = addButton.textContent;
        const formData = new FormData(form);
        const quantityRaw = formData.get('quantity');
        const quantity = quantityRaw ? Math.max(1, parseInt(String(quantityRaw), 10) || 1) : 1;

        const mainItem = {
            id: mainVariantId,
            quantity,
        };

        const mainItemProperties = {};
        formData.forEach((value, key) => {
            if (!value || typeof key !== 'string') return;
            const propertyMatch = /^properties\[(.+)]$/.exec(key);
            if (!propertyMatch) return;
            mainItemProperties[propertyMatch[1]] = String(value);
        });

        if (Object.keys(mainItemProperties).length) {
            mainItem.properties = mainItemProperties;
        }

        addButton.disabled = true;
        addButton.textContent = 'Adding...';

        const items = [
            mainItem,
            {
                id: freeSockVariantId,
                quantity: 1,
                properties: {
                    [propertyKey]: propertyValue,
                },
            },
        ];

        try {
            if (typeof window.liquidAjaxCart?.add === 'function') {
                window.liquidAjaxCart.add({ items });
            } else {
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({ items }),
                });
            }
        } finally {
            addButton.disabled = false;
            addButton.textContent = previousButtonText;
        }
    }

    updateVariantSelection(productContainer) {
        const selects = productContainer.querySelectorAll('.eam-cc-addon-variant-select');
        const freeSockSelect = productContainer.querySelector('.eam-cc-free-sock-variant-select');
        const addButton = productContainer.querySelector('.eam-cc-addon-product-button');
        const hasFreeSockFlow = addButton?.dataset?.addWithFreeSock === 'true';

        if (selects.length === 0) {
            if (hasFreeSockFlow && addButton) {
                addButton.disabled = !freeSockSelect?.value;
            }
            return;
        }

        const productId = selects[0].dataset.productId;
        
        let productData = this.getProductData(productId, productContainer);
        if (!productData) {
            return;
        }

        const selectedOptions = [];
        let allSelected = true;

        productData.options.forEach((optionName, index) => {
            const select = Array.from(selects).find(s => s.dataset.optionName === optionName);
            if (select && select.value) {
                selectedOptions[index] = select.value;
            } else {
                allSelected = false;
                selectedOptions[index] = null;
            }
        });

        const variantIdInput = productContainer.querySelector('.eam-cc-addon-variant-id');
        const preorderPropertyInput = productContainer.querySelector('.eam-cc-addon-preorder-property');

        if (!allSelected) {
            if (variantIdInput) {
                variantIdInput.value = '';
                variantIdInput.disabled = true;
            }
            if (preorderPropertyInput) {
                preorderPropertyInput.value = '';
                preorderPropertyInput.disabled = true;
            }
            if (addButton) {
                addButton.disabled = true;
                addButton.textContent = this.buttonText;
            }
            return;
        }

        const matchingVariant = productData.variants.find(variant => {
            return variant.options.every((option, index) => option === selectedOptions[index]);
        });

        if (matchingVariant) {
            if (variantIdInput) {
                variantIdInput.value = matchingVariant.id;
                variantIdInput.disabled = false;
            }

            const selectedOption = this.getSelectedOptionElement(productContainer, selectedOptions);
            const isPreorder = selectedOption?.dataset.preorderEnabled === 'true';
            const deliveryDate = selectedOption?.dataset.preorderDelivery || '';
            const customAtcText = selectedOption?.dataset.atcText || this.buttonText;

            if (preorderPropertyInput) {
                if (isPreorder && deliveryDate) {
                    preorderPropertyInput.value = 'Estimated delivery date is ' + deliveryDate.trim() + '.';
                    preorderPropertyInput.disabled = false;
                } else {
                    preorderPropertyInput.value = '';
                    preorderPropertyInput.disabled = true;
                }
            }

            if (addButton) {
                if (matchingVariant.available) {
                    addButton.disabled = false;
                    addButton.removeAttribute('disabled');
                    addButton.textContent = isPreorder ? customAtcText : this.buttonText;
                    if (hasFreeSockFlow && !freeSockSelect?.value) {
                        addButton.disabled = true;
                    }
                } else {
                    addButton.disabled = true;
                    addButton.textContent = 'Out Of Stock';
                }
            }
        } else {
            if (variantIdInput) {
                variantIdInput.value = '';
                variantIdInput.disabled = true;
            }
            if (preorderPropertyInput) {
                preorderPropertyInput.value = '';
                preorderPropertyInput.disabled = true;
            }
            if (addButton) {
                addButton.disabled = true;
                addButton.textContent = 'Unavailable';
            }
        }
    }

    getSelectedOptionElement(productContainer, selectedOptions) {
        const selects = productContainer.querySelectorAll('.eam-cc-addon-variant-select');
        
        for (let i = selectedOptions.length - 1; i >= 0; i--) {
            if (selectedOptions[i]) {
                const select = Array.from(selects).find(s => {
                    const optionName = s.dataset.optionName;
                    const productData = this.getProductData(s.dataset.productId, productContainer);
                    return productData && productData.options[i] === optionName;
                });
                
                if (select) {
                    const selectedOption = select.querySelector(`option[value="${selectedOptions[i]}"]`);
                    if (selectedOption && selectedOption.dataset.preorderEnabled) {
                        return selectedOption;
                    }
                }
            }
        }
        
        for (const select of selects) {
            if (select.value) {
                const selectedOption = select.querySelector(`option[value="${select.value}"]`);
                if (selectedOption && selectedOption.dataset.preorderEnabled) {
                    return selectedOption;
                }
            }
        }
        
        return null;
    }

    getProductData(productId, productContainer) {
        if (window.cartAddonVariants?.[productId]) {
            return window.cartAddonVariants[productId];
        }

        const scriptTag = productContainer.querySelector('script');
        if (scriptTag) {
            try {
                eval(scriptTag.textContent);
                if (window.cartAddonVariants?.[productId]) {
                    return window.cartAddonVariants[productId];
                }
            } catch (error) {
                
            }
        }

        const allScripts = document.querySelectorAll('script');
        for (let script of allScripts) {
            if (script.textContent.includes(`window.cartAddonVariants[${productId}]`)) {
                try {
                    eval(script.textContent);
                    if (window.cartAddonVariants?.[productId]) {
                        return window.cartAddonVariants[productId];
                    }
                } catch (error) {
                    
                }
            }
        }

        const optionNames = [];
        const selects = productContainer.querySelectorAll('.eam-cc-addon-variant-select');
        selects.forEach(select => {
            const optionName = select.dataset.optionName;
            if (optionName && !optionNames.includes(optionName)) {
                optionNames.push(optionName);
            }
        });

        if (optionNames.length > 0) {
            return {
                options: optionNames,
                variants: []
            };
        }

        return null;
    }
}

customElements.define('cart-addon-products', CartAddonProducts);